import type { Route } from "./+types/signup-dialog-route";
import { Button } from "@www/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@www/ui/dialog";
import { LogIn, LogInIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  data,
  useFetcher,
} from "react-router";
import { getToast } from "remix-toast";
import { toast } from "sonner";
import {
  AuthOptionsButtonList,
  VerifyEmailOTPView,
} from "~/components/auth-forms";
import { type Logger } from "~/logging";
import {
  actionWithDefaultErrorHandling,
  getActionIntent,
} from "~/utils/actions.server";
import { sendEmailCode } from "./actions/send-email-code";
import { verifyEmailCode } from "./actions/verify-email-code";
import { isFormValidationActionError } from "~/utils/response";
import { createLoaderLogger } from "~/utils/loaders.server";
import { requireRerouteIfLoggedIn } from "~/utils/auth/loaders.server";
import { useGA4PageView } from "~/hooks/analytics-tracking";

export enum DemoSignupRouteIntent {
  SEND_EMAIL_CODE = "demo_signup_route_send_email_code",
  VERIFY_CODE = "demo_signup_route_send_verify_code",
}

export const loader = async (args: LoaderFunctionArgs) => {
  const logger = createLoaderLogger(args);
  await requireRerouteIfLoggedIn({
    logger,
    args,
    redirectPath: "/map/preview/preferences",
  });

  const { toast, headers } = await getToast(args.request);
  return data({ toast }, { headers });
};

export const action = actionWithDefaultErrorHandling(
  async (args: ActionFunctionArgs, logger: Logger) => {
    const intent = await getActionIntent(args.request);

    if (intent === DemoSignupRouteIntent.SEND_EMAIL_CODE)
      return await sendEmailCode(args, logger);
    if (intent === DemoSignupRouteIntent.VERIFY_CODE)
      return await verifyEmailCode(args, logger);
  },
);

export default function SignupDialogRoute({
  loaderData,
}: Route.ComponentProps) {
  const [step, setStep] = useState<"intro" | "signup">("intro");

  const [showEmailOTPView, setShowEmailOTPView] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  useGA4PageView();

  useEffect(() => {
    if (loaderData.toast?.type === "error") {
      toast.error(loaderData.toast.message);
    }
    if (loaderData.toast?.type === "info") {
      toast.info(loaderData.toast.message);
    }
  }, [loaderData.toast]);

  const sendEmailCodeFetcher = useFetcher<typeof sendEmailCode>({
    key: DemoSignupRouteIntent.SEND_EMAIL_CODE,
  });

  const verifyCodeFetcher = useFetcher<typeof verifyEmailCode>({
    key: DemoSignupRouteIntent.VERIFY_CODE,
  });

  useEffect(() => {
    if (
      sendEmailCodeFetcher.state === "idle" &&
      sendEmailCodeFetcher.data !== undefined &&
      "success" in sendEmailCodeFetcher.data &&
      sendEmailCodeFetcher.data.success === true
    ) {
      setShowEmailOTPView(true);
    }
  }, [sendEmailCodeFetcher.state, sendEmailCodeFetcher.data]);

  if (step === "intro")
    return (
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Try our free demo
          </DialogTitle>
          <DialogDescription className="pt-4 text-base">
            <p className="mb-2">
              Create a free account to access our demo. It only takes two
              minutes.
            </p>
            <p>
              This demo unlocks 100% of LandFinder's capabilities for a small
              subsection of Florida.
            </p>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
          <Button
            variant="default"
            className="w-full gap-2 sm:w-auto"
            onClick={() => setStep("signup")}
          >
            <LogIn className="mr-2 h-4 w-4" />
            Create Free Account
          </Button>
        </DialogFooter>
      </DialogContent>
    );

  if (showEmailOTPView)
    return (
      <DialogContent>
        <VerifyEmailOTPView
          fullHeight={false}
          email={email}
          name={name}
          formValidationErrors={
            isFormValidationActionError(verifyCodeFetcher.data)
              ? verifyCodeFetcher.data.formValidationError
              : undefined
          }
          verifyCodeIntent={DemoSignupRouteIntent.VERIFY_CODE}
          onCancel={() => setShowEmailOTPView(false)}
        />
      </DialogContent>
    );

  return (
    <DialogContent className="sm:max-w-md">
      <div className="mx-auto flex max-w-xs flex-col items-center justify-center">
        <div className="flex flex-col items-center space-y-6">
          <div className="bg-foreground flex h-16 w-16 items-center justify-center rounded-full">
            <LogInIcon className="text-base-100 h-8 w-8" />
          </div>
          <h1 className="text-2xl font-semibold">Create your account</h1>
          <AuthOptionsButtonList
            email={email}
            name={name}
            mode="signup"
            formValidationErrors={
              isFormValidationActionError(sendEmailCodeFetcher.data)
                ? sendEmailCodeFetcher.data.formValidationError
                : undefined
            }
            sendEmailCodeIntent={DemoSignupRouteIntent.SEND_EMAIL_CODE}
            onEmailChange={setEmail}
            onNameChange={setName}
          />
          <p className="text-muted-foreground text-center text-sm">
            By signing up, you agree to our{" "}
            <a href="TODO" className="text-foreground">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="TODO" className="text-foreground">
              Data Processing Agreement
            </a>
            .
          </p>
        </div>
      </div>
    </DialogContent>
  );
}
